// src/page/UserList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';

export default function UserList() {
    useAuthGuard();
    const navigate = useNavigate();

    // ===== URL state (for Back/Forward persistence) =====
    const [sp, setSp] = useSearchParams();
    const initPage = Number(sp.get('page') || 1);
    const initSize = Number(sp.get('size') || 20);
    const initQ = sp.get('q') || '';
    const initTab = sp.has('tab') ? Number(sp.get('tab')) : -1; // -1 = all
    const initSList = sp.get('s') ? sp.get('s').split(',').map(Number).filter(n => [0, 1].includes(n)) : [];
    const initSortKey = sp.get('sort') || 'created'; // number|email|name|created|deleted|level
    const initSortDir = sp.get('dir') || 'desc';  // asc|desc

    // ===== UI / Query state =====
    // typing buffer
    const [keyword, setKeyword] = useState(initQ);
    // committed value (used for fetch & URL)
    const [queryKeyword, setQueryKeyword] = useState(initQ);
    const [statusChecks, setStatusChecks] = useState({
        active: initSList.includes(0),    // 有効
        withdrawn: initSList.includes(1), // 退会
    });

    const [s, setS] = useState(initTab);
    const [querySList, setQuerySList] = useState(initSList);

    const [sort, setSort] = useState({ key: initSortKey, dir: initSortDir });
    const toggleSort = (key) =>
        setSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

    const [page, setPage] = useState(initPage);
    const [pageSize, setPageSize] = useState(initSize);

    // ===== Data buckets =====
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [counts, setCounts] = useState({ all: 0, active: 0, withdrawn: 0 });
    const [headerCounts, setHeaderCounts] = useState({ all: 0, active: 0, withdrawn: 0 });

    // derived (pagination UI)
    const pageCount = Math.max(1, Math.ceil((total || 0) / pageSize));
    const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const last = Math.min(total, page * pageSize);
    const pages = useMemo(() => Array.from({ length: pageCount }, (_, i) => i + 1), [pageCount]);

    // ===== Sync state -> URL =====
    useEffect(() => {
        const p = new URLSearchParams();
        if (page !== 1) p.set('page', String(page));
        if (pageSize !== 20) p.set('size', String(pageSize));
        if (queryKeyword) p.set('q', queryKeyword);

        if (querySList.length) p.set('s', querySList.join(','));
        else if (s >= 0) p.set('tab', String(s));

        if (sort.key) p.set('sort', sort.key);
        if (sort.dir) p.set('dir', sort.dir);

        setSp(p, { replace: true });
    }, [page, pageSize, queryKeyword, querySList, s, sort, setSp]);

    // ===== Fetch list =====
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setLoading(true);
                setError('');
                const token = localStorage.getItem('access_token');

                const url = new URL(`${API_BASE_URL}/v1/user`);
                url.searchParams.set('page', String(page));
                url.searchParams.set('size', String(pageSize));
                url.searchParams.set('sort', sort.key);
                url.searchParams.set('dir', sort.dir);

                if (querySList.length > 0) url.searchParams.set('s', querySList.join(','));
                else if (s >= 0) url.searchParams.set('s', String(s));

                if (queryKeyword.trim() !== '') url.searchParams.set('q', queryKeyword.trim());

                const res = await fetch(url.toString(), {
                    headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
                    signal: ac.signal,
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();

                setItems(Array.isArray(data.items) ? data.items : []);
                setTotal(Number(data.total) || 0);

                if (data.counts) {
                    setCounts(data.counts);
                    const noFilter = (keyword.trim() === '') && (querySList.length === 0) && (s === -1);
                    if (noFilter) setHeaderCounts(data.counts);
                }

                if (data.size && Number(data.size) !== pageSize) setPageSize(Number(data.size));
            } catch (e) {
                if (e.name !== 'AbortError') setError('会員の取得に失敗しました');
            } finally {
                setLoading(false);
            }
        })();
        return () => ac.abort();
    }, [page, pageSize, s, queryKeyword, querySList, sort]);

    // ===== Keep page in range =====
    useEffect(() => {
        if (page > pageCount) setPage(pageCount);
        if (page < 1) setPage(1);
    }, [page, pageCount]);

    // ===== Handlers =====
    const activateTab = (nextS) => {
        // enter "tab mode": clear checkbox filter list so URL uses ?tab=
        setQuerySList([]);
        // sync visual checkboxes with the chosen tab
        if (nextS === 0) setStatusChecks({ active: true, withdrawn: false });
        else if (nextS === 1) setStatusChecks({ active: false, withdrawn: true });
        else setStatusChecks({ active: false, withdrawn: false }); // -1 (all)
        setS(nextS);
        setPage(1);
    };

    // Keep checkboxes synced when tab `s` changes (e.g., via URL/back/forward) and we're in tab mode
    useEffect(() => {
        if (querySList.length > 0) return; // checkbox mode; don't override user choice
        if (s === 0) setStatusChecks({ active: true, withdrawn: false });
        else if (s === 1) setStatusChecks({ active: false, withdrawn: true });
        else setStatusChecks({ active: false, withdrawn: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [s, querySList.length]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const selected = [];
        if (statusChecks.active) selected.push(0);
        if (statusChecks.withdrawn) selected.push(1);
        setQuerySList(selected);
        setQueryKeyword(keyword.trim());
        setPage(1);
    };

    const handleClear = () => {
        setKeyword('');
        setQueryKeyword('');
        setStatusChecks({ active: false, withdrawn: false });
        setQuerySList([]);
        setS(-1);
        setPage(1);
    };

    const goPrev = () => setPage(p => Math.max(1, p - 1));
    const goNext = () => setPage(p => Math.min(pageCount, p + 1));

    // add this inside the component body, e.g. above the return(...)
    const handleExportCsv = async () => {
        try {
            const token = localStorage.getItem('access_token') || '';

            // build query same as the list API
            const url = new URL(`${API_BASE_URL}/v1/user/export`);
            url.searchParams.set('sort', sort.key);
            url.searchParams.set('dir', sort.dir);

            if (querySList.length > 0) {
                url.searchParams.set('s', querySList.join(','));
            } else if (s >= 0) {
                url.searchParams.set('s', String(s));
            }

            if (queryKeyword.trim() !== '') {
                url.searchParams.set('q', queryKeyword.trim());
            }

            const res = await fetch(url.toString(), {
                headers: {
                    Accept: 'text/csv',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
            });

            if (!res.ok) {
                // try get server error message
                let msg = `Export failed (HTTP ${res.status})`;
                try {
                    const j = await res.json();
                    if (j?.error) msg = j.error;
                } catch {
                    /* ignore */
                }
                throw new Error(msg);
            }

            const blob = await res.blob();

            // pick filename from Content-Disposition
            const cd = res.headers.get('Content-Disposition') || '';
            const m = cd.match(/filename\*?=(?:UTF-8''|")(.*?)(?:\"|$)/i);
            let filename = m ? decodeURIComponent(m[1]) : `会員一覧-${new Date().toISOString().replace(/[-:T.Z]/g, '').slice(0, 14)}.csv`;

            // download
            const href = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = href;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(href);
        } catch (e) {
            console.error(e);
            setError('CSVのダウンロードに失敗しました');
        }
    };

    // ===== Helpers =====
    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (s) => {
        if (!s) return '-';
        const d = new Date(s);
        if (isNaN(d)) return s;
        return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    const statusLabel = (it) => (it.deletedAt ? '退会' : '有効');

    // ===== Render =====
    return (
        <Layout>
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item"><a href="/user">会員管理</a></li>
                    <li className="breadcrumb-item active">会員一覧</li>
                </ol>
            </nav>

            <div className="mb-9">
                <div className="row g-3 mb-4">
                    <div className="col-auto">
                        <h2 className="mb-0">会員一覧</h2>
                    </div>
                </div>

                {/* Tabs */}
                <ul className="nav nav-links mb-3 mb-lg-2 mx-n3">
                    <li className="nav-item">
                        <a href="#"
                            aria-current={s === -1 ? 'page' : undefined}
                            className={`nav-link ${s === -1 ? 'active' : ''}`}
                            onClick={(e) => { e.preventDefault(); activateTab(-1); }}>
                            <span>全て </span>
                            <span className="text-body-tertiary fw-semibold">({headerCounts.all})</span>
                        </a>
                    </li>
                    <li className="nav-item">
                        <a href="#"
                            className={`nav-link ${s === 0 ? 'active' : ''}`}
                            onClick={(e) => { e.preventDefault(); activateTab(0); }}>
                            <span>有効 </span>
                            <span className="text-body-tertiary fw-semibold">({headerCounts.active})</span>
                        </a>
                    </li>
                    <li className="nav-item">
                        <a href="#"
                            className={`nav-link ${s === 1 ? 'active' : ''}`}
                            onClick={(e) => { e.preventDefault(); activateTab(1); }}>
                            <span>退会 </span>
                            <span className="text-body-tertiary fw-semibold">({headerCounts.withdrawn})</span>
                        </a>
                    </li>
                </ul>

                <div id="users">
                    <div className="mb-4">
                        <form className="position-relative w-100" onSubmit={handleSubmit}>
                            <div className="d-flex flex-wrap gap-3 align-items-end">
                                {/* Keyword */}
                                <div className="search-box">
                                    <input
                                        aria-label="Search"
                                        className="form-control search-input search"
                                        placeholder="会員番号/メールアドレス/氏名"
                                        type="search"
                                        name="keyword"
                                        value={keyword}
                                        onChange={(e) => setKeyword(e.target.value)}
                                    />
                                    <span className="fas fa-search search-box-icon"></span>
                                </div>

                                {/* Status checks */}
                                <div>
                                    <label className="form-label mb-1 d-block">ステータス</label>
                                    <div className="d-flex flex-wrap gap-2">
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" id="status-active"
                                                checked={statusChecks.active}
                                                onChange={(e) => setStatusChecks(v => ({ ...v, active: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-active">有効</label>
                                        </div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" id="status-withdrawn"
                                                checked={statusChecks.withdrawn}
                                                onChange={(e) => setStatusChecks(v => ({ ...v, withdrawn: e.target.checked }))} />
                                            <label className="form-check-label" htmlFor="status-withdrawn">退会</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex gap-2 mt-3">
                                <button type="submit" className="btn btn-primary">検索</button>
                                <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>クリア</button>

                                <div className="ms-xxl-auto d-flex gap-3">
                                    <button className="btn btn-link text-body me-4 px-0" type="button" onClick={handleExportCsv}>
                                        <span className="fa-solid fa-file-export fs-9 me-2"></span>CSVダウンロード
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                        <div className="table-responsive scrollbar mx-n1 px-1">
                            <table className="table fs-9 mb-0">
                                <thead>
                                    <tr>
                                        <th className="align-middle ps-3 text-nowrap">会員ID</th>
                                        <th className="align-middle ps-3 text-nowrap" role="button"
                                            onClick={() => { toggleSort('number'); setPage(1); }}>
                                            会員番号 <span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="align-middle ps-3 text-nowrap">メールアドレス</th>
                                        <th className="align-middle ps-3 text-nowrap" role="button">氏名（漢字）</th>
                                        <th className="align-middle ps-3 text-nowrap">氏名（カタカナ）</th>
                                        <th className="align-middle ps-3 text-nowrap" role="button"
                                            onClick={() => { toggleSort('level'); setPage(1); }}>
                                            ユーザーレベル <span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="align-middle ps-3 text-nowrap">プッシュ通知</th>
                                        <th className="align-middle ps-3" role="button"
                                            onClick={() => { toggleSort('deleted'); setPage(1); }}>
                                            退会日時 <span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="align-middle ps-3 text-nowrap" role="button"
                                            onClick={() => { toggleSort('created'); setPage(1); }}>
                                            登録日時 <span className="fas ms-1 text-muted fa-sort" />
                                        </th>
                                        <th className="align-middle ps-3"></th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {loading && (
                                        <tr><td colSpan={10} className="text-center py-4">読み込み中...</td></tr>
                                    )}
                                    {!loading && error && (
                                        <tr><td colSpan={10} className="text-danger py-4">{error}</td></tr>
                                    )}
                                    {!loading && !error && items.length === 0 && (
                                        <tr><td colSpan={10} className="text-center py-4">データがありません</td></tr>
                                    )}
                                    {!loading && !error && items.map((it) => (
                                        <tr key={it.userId}>
                                            <td className='text-nowrap'>{it.userId || '-'}</td>
                                            <td>{it.userNumber || '-'}</td>
                                            <td>{it.b2cEmail || '-'}</td>
                                            <td>{[(it.lastName || ''), (it.firstName || '')].join(' ').trim() || '-'}</td>
                                            <td>{[(it.lastNameKana || ''), (it.firstNameKana || '')].join(' ').trim() || '-'}</td>
                                            <td>{Number.isFinite(it.userLevel) ? it.userLevel : (it.userLevel ?? '-')}</td>
                                            <td>{it.notificationEnabledFlag ? 'ON' : 'OFF'}</td>
                                            <td>{fmt(it.deletedAt)}</td>
                                            <td>{fmt(it.createdAt)}</td>
                                            <td>
                                                <button className="btn btn-sm btn-outline-primary text-nowrap"
                                                    onClick={() => navigate(`/user/${it.userId}`)}>
                                                    詳細
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="row align-items-center justify-content-between py-2 pe-0 fs-9">
                            <div className="col-auto d-flex align-items-center gap-3">
                                <p className="mb-0 d-none d-sm-block me-1 fw-semibold text-body">
                                    {total === 0 ? '0 items' : `${first} to ${last} items of ${total}`}
                                </p>
                            </div>

                            <div className="col-auto d-flex align-items-center">
                                <button type="button" className="page-link" onClick={goPrev} disabled={page <= 1}>
                                    <span className="fas fa-chevron-left" />
                                </button>

                                <ul className="mb-0 pagination">
                                    {pages.map(p => (
                                        <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                                            <button type="button" className="page-link" onClick={() => setPage(p)}>
                                                {p}
                                            </button>
                                        </li>
                                    ))}
                                </ul>

                                <button type="button" className="page-link pe-0" onClick={goNext} disabled={page >= pageCount}>
                                    <span className="fas fa-chevron-right" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
